import { Action, configureStore, Middleware, Reducer } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist';
import createCompressTransform from 'redux-persist-transform-compress';
import { ChatState } from '../components/chat/store/types';
import { FinancialFilterSlice } from '../components/financialFilter/store/type';
import { ActionsState } from '../screens/actionList/store/types';
import { AuthSlice, logout } from '../screens/auth/store/auth.slice';
import { CameraState } from '../screens/camera/store/types';
import { CourierState } from '../screens/courier/store/types';
import { FinancialMenuState } from '../screens/finOrders/state/types';
import { InventoryStore } from '../screens/inventory/store/types';
import { MonitoringStore } from '../screens/monitoring/store/types';
import { OrderStore } from '../screens/monitoringOrder/store/types';
import { CallState } from '../screens/phoneCall/store/types';
import { IPhotoReportState } from '../screens/photoReport/store/types';
import { QuizState } from '../screens/quiz/store/quiz.slice';
import { ReportStore } from '../screens/report/store/types';
import { ReviewState } from '../screens/reviews/store/types';
import { EmployeeState } from '../screens/selectEmployee/store/types';
import { TableState } from '../screens/tableView/store/types';
import { TaskSwitcherState } from '../screens/taskCreate/store/types';
import { TaskMenuState } from '../screens/taskTypes/store/types';
import { TimeControlState } from '../screens/timeControl/store/types';
import { api, apiAI, apiAIAssistant, apiArchive, apiCourier, apiEvotor, apiPush, apiStreamServer } from './api';
import { AppState } from './app.slice';
import { appEnv } from '../config/env';
import { rootReducer } from './rootReducer';
// @ts-ignore
import { encryptTransform } from 'redux-persist-transform-encrypt';
import { SelectStore } from '../components/selectOblect/state/types';
import { ExternalVideoState } from '../screens/externalСamera/store/types';
import { FinancialState } from '../screens/financial/store/types';
import { LoyaltyState } from '../screens/loyalty/store/types';
import { ProductState } from '../screens/products/store/types';
import { ProductZakupState } from '../screens/purchase/store/types';
import { AnaliticStore } from '../screens/videoStreams/store/types';
import { WaitersState } from '../screens/waiters/store/types';
import { idbStorage } from './storage';

const persistConfig = {
  key: 'ifood',
  storage: idbStorage,
  blacklist: ['chat', 'loyalty', 'reviews', 'photoReport', 'analitic'],
  transforms: [
    createCompressTransform(),
    encryptTransform({
      secretKey: appEnv.storageKey,
    }),
  ],
};

interface StateReducer {
  app: AppState;
  auth: AuthSlice;
  quiz: QuizState;
  camera: CameraState;
  taskMenu: TaskMenuState;
  financialMenu: FinancialMenuState;
  financialFilter: FinancialFilterSlice;
  chat: ChatState;
  report: ReportStore;
  taskSwitch: TaskSwitcherState;
  inventory: InventoryStore;
  actions: ActionsState;
  photoReport: IPhotoReportState;
  employee: EmployeeState;
  call: CallState;
  table: TableState;
  monitor: MonitoringStore;
  order: OrderStore;
  timeControl: TimeControlState;
  reviews: ReviewState;
  courier: CourierState;
  loyalty: LoyaltyState;
  product: ProductState;
  financial: FinancialState;
  select: SelectStore;
  purchase: ProductZakupState;
  video: ExternalVideoState;
  waiters: WaitersState;
  analitic: AnaliticStore;
}

const appReducer: Reducer<StateReducer | undefined, Action> = (state: any, action) => {
  if (action.type === logout.type) {
    state = undefined;
  }
  return rootReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, appReducer);

const clearPersistOnLogout: Middleware = () => (next) => (action) => {
  if (action.type === logout.type) {
    void idbStorage.removeItem('persist:ifood');
  }
  return next(action);
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: process.env.NODE_ENV !== 'production',
      serializableCheck:
        process.env.NODE_ENV === 'production'
          ? false
          : {
              ignoredActions: [
                FLUSH,
                REHYDRATE,
                PAUSE,
                PERSIST,
                PURGE,
                REGISTER,
              ],
            },
    }).concat(
      clearPersistOnLogout,
      api.middleware as any,
      apiPush.middleware as any,
      apiAI.middleware as any,
      apiAIAssistant.middleware as any,
      apiCourier.middleware as any,
      apiStreamServer.middleware as any,
      apiArchive.middleware as any,
      apiEvotor.middleware as any
    ),
});

export const persistor = persistStore(store);

setupListeners(store.dispatch);
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
